import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UploadSimple, X } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useLinkedTab } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { fetchAllPages } from '@/lib/fetch-all-pages';
import { mergeWhere } from '@/lib/table-query';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { ManageAttendanceFilters } from '../../components/manage-attendance-filters/manage-attendance-filters';
import { Button } from '@/components/ui-kit/button';
import { Label } from '@/components/ui-kit/label';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  canTakeLunch,
  getOrgDateString,
} from '@/modules/attendance/hr-my-attendance/services/attendance-rules';
import { useAttendancePolicy } from '@/modules/attendance/hr-my-attendance/hooks/use-attendance-policy';
import {
  createAttendanceTableColumns,
  createRosterTableColumns,
} from '../../components/attendance-table-columns/attendance-table-columns';
import {
  createCorrectionRequestsTableColumns,
  isReviewable,
} from '../../components/correction-requests-table/correction-requests-table-columns';
import { BulkReviewDialog } from '../../components/bulk-review-dialog/bulk-review-dialog';
import { AttendanceExportButtons } from '../../components/attendance-export/attendance-export-buttons';
import { ImportAttendanceDialog } from '../../components/import-attendance-dialog/import-attendance-dialog';
import {
  useCurrentReviewer,
  useGetAttendanceCorrections,
  useGetAttendanceRoster,
  useGetAttendances,
  useReviewAttendanceCorrection,
  useSearchCorrectionEmployees,
  useWaiveCorrectionFine,
} from '../../hooks/use-manage-attendance';
import {
  DEFAULT_ATTENDANCE_CORRECTIONS_ORDER,
  DEFAULT_ATTENDANCES_ORDER,
  DEFAULT_ROSTER_ORDER,
} from '../../graphql/queries';
import { getAttendanceRoster, getAttendances } from '../../services/manage-attendance.service';
import {
  buildCorrectionFilter,
  buildManageAttendanceFilter,
  containsFilter,
} from '../../utils/manage-attendance-filter';
import {
  downloadAttendanceCsv,
  downloadAttendancePdf,
  exportRowsOfRecords,
  exportRowsOfRoster,
} from '../../utils/attendance-export';
import {
  AttendanceCorrection,
  AttendanceCorrectionsPagingResult,
  AttendanceRecord,
  AttendancesPagingResult,
  RosterRow,
  EMPTY_MANAGE_ATTENDANCE_FILTERS,
  ManageAttendanceFilters as ManageAttendanceFiltersValue,
} from '../../types/manage-attendance.types';

/** An export reads the list page by page, bounded so a huge filter can't hang the browser. */
const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_ROWS = 5000;

/**
 * With a day picked (today by default) the tab lists every employee in scope for that day, with or
 * without a record (the legacy view); with no day it lists the records of every day. Each view has
 * its own paging, sorting and column filters: the roster's sort and filter employees, the list's
 * records.
 */
const AttendanceTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canExport = useHasPermission(HRM_PERMISSIONS.ATTENDANCE.EXPORT);
  const canImport = useHasPermission(HRM_PERMISSIONS.ATTENDANCE.IMPORT);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // The spot beside the day picker the cards' filter button is rendered into on a phone.
  const [filterSlot, setFilterSlot] = useState<HTMLSpanElement | null>(null);
  // Break time only for reviewers who can take breaks or lunch themselves.
  const policy = useAttendancePolicy();
  const showBreak = policy.canTakeBreak || canTakeLunch(policy);
  const today = getOrgDateString(new Date());
  const listTable = useServerTable({ pageSize: 15, defaultOrder: DEFAULT_ATTENDANCES_ORDER });
  const rosterTable = useServerTable({ pageSize: 15, defaultOrder: DEFAULT_ROSTER_ORDER });

  const [filters, setFilters] = useState<ManageAttendanceFiltersValue>({
    ...EMPTY_MANAGE_ATTENDANCE_FILTERS,
    date: today,
  });
  const { departments, getDepartmentIdCondition } = useDepartmentScope();
  const handleFiltersChange = (next: ManageAttendanceFiltersValue) => {
    setFilters(next);
    listTable.resetPage();
    rosterTable.resetPage();
  };

  const isRoster = Boolean(filters.date);
  const departmentCondition = getDepartmentIdCondition(filters.departmentId);
  const recordFilter = mergeWhere(
    buildManageAttendanceFilter(filters, departmentCondition),
    listTable.columnWhere
  );
  const employeeFilter = mergeWhere(
    {
      ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
      ...(filters.keyword.trim()
        ? {
            $or: [
              { DisplayName: containsFilter(filters.keyword) },
              { Email: containsFilter(filters.keyword) },
            ],
          }
        : {}),
    },
    rosterTable.columnWhere
  );

  const list = useGetAttendances(
    {
      pageNo: listTable.pagination.pageIndex + 1,
      pageSize: listTable.pagination.pageSize,
      filter: recordFilter,
      order: listTable.order,
    },
    !isRoster
  );
  const roster = useGetAttendanceRoster(
    {
      day: filters.date,
      employeeFilter,
      pageNo: rosterTable.pagination.pageIndex + 1,
      pageSize: rosterTable.pagination.pageSize,
      order: rosterTable.order,
    },
    isRoster
  );
  const listResult = (list.data as { Attendances?: AttendancesPagingResult })?.Attendances;
  // Records carry no email; roster rows get it with the employee.
  const { byUserId } = useEmployeesByUserIds(
    isRoster ? [] : (listResult?.items ?? []).map((record) => record.UserId)
  );
  const active = isRoster ? roster : list;

  // The export follows what the table shows: the same filters, column filters and order.
  const handleExport = async (format: 'csv' | 'pdf') => {
    const rows = isRoster
      ? exportRowsOfRoster(
          await fetchAllPages(
            async (pageNo, pageSize) => {
              const page = await getAttendanceRoster({
                day: filters.date,
                employeeFilter,
                pageNo,
                pageSize,
                order: rosterTable.order,
              });
              return { items: page.rows, totalCount: page.totalCount };
            },
            EXPORT_PAGE_SIZE,
            EXPORT_MAX_ROWS
          ),
          filters.date
        )
      : exportRowsOfRecords(
          await fetchAllPages(
            async (pageNo, pageSize) => {
              const page = await getAttendances({
                pageNo,
                pageSize,
                filter: recordFilter,
                order: listTable.order,
              });
              return { items: page.Attendances.items, totalCount: page.Attendances.totalCount };
            },
            EXPORT_PAGE_SIZE,
            EXPORT_MAX_ROWS
          )
        );
    const scope = filters.date || 'all-days';
    if (format === 'csv') downloadAttendanceCsv(rows, t, scope);
    else downloadAttendancePdf(rows, t, scope, `${t('MANAGE_ATTENDANCE')} ${filters.date}`);
  };

  const table = isRoster ? (
    <AdvanceDataTable
      {...rosterTable.tableProps}
      data={roster.data?.rows ?? []}
      columns={createRosterTableColumns({ t, showBreak, departments })}
      onRowClick={(row: RosterRow) => navigate(`/hr/manage-attendance/${row.employee.UserId}`)}
      isLoading={roster.isLoading}
      error={roster.error instanceof Error ? roster.error : null}
      pagination={{
        pageIndex: rosterTable.pagination.pageIndex,
        pageSize: rosterTable.pagination.pageSize,
        totalCount: roster.data?.totalCount ?? 0,
      }}
      isExpandRowContent={false}
      mobileView="cards"
      mobileFilterSlot={filterSlot}
    />
  ) : (
    <AdvanceDataTable
      {...listTable.tableProps}
      data={listResult?.items ?? []}
      columns={createAttendanceTableColumns({
        t,
        emailOf: (userId) => byUserId.get(userId)?.Email,
        showBreak,
        departments,
      })}
      onRowClick={(record: AttendanceRecord) => navigate(`/hr/manage-attendance/${record.UserId}`)}
      isLoading={list.isLoading}
      error={list.error instanceof Error ? list.error : null}
      pagination={{
        pageIndex: listTable.pagination.pageIndex,
        pageSize: listTable.pagination.pageSize,
        totalCount: listResult?.totalCount ?? 0,
      }}
      isExpandRowContent={false}
      mobileView="cards"
      mobileFilterSlot={filterSlot}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {/* On a phone the day picker has its own row, with Import, CSV and PDF sharing the row under it. */}
      <div className="flex flex-wrap items-end justify-between gap-2 max-md:flex-col max-md:items-stretch">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="min-w-0 flex-1">
            <ManageAttendanceFilters
              value={filters}
              onChange={handleFiltersChange}
              showDate
              maxDate={today}
            />
          </div>
          {/* On a phone the cards' filter button sits here, beside the day picker. */}
          <span ref={setFilterSlot} className="contents md:hidden" />
        </div>
        <div className="flex gap-2 max-md:w-full max-md:[&>div]:contents max-md:[&_button]:flex-1">
          {canImport && (
            <Button
              variant="outline"
              title={t('IMPORT')}
              aria-label={t('IMPORT')}
              onClick={() => setIsImportOpen(true)}
            >
              <UploadSimple className="size-4" />
              {t('IMPORT')}
            </Button>
          )}
          {canExport &&
            (active.isLoading ? (
              // Hold the buttons' place while the list loads (they export what it holds).
              <div className="flex gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
              </div>
            ) : (
              <AttendanceExportButtons onExport={handleExport} />
            ))}
        </div>
      </div>
      {table}
      <ImportAttendanceDialog open={isImportOpen} onOpenChange={setIsImportOpen} />
    </div>
  );
};

const CorrectionRequestsTab = () => {
  const { t } = useTranslation();
  const canReview = useHasPermission(HRM_PERMISSIONS.ATTENDANCE.REVIEW_CORRECTION);
  const canWaive = useHasPermission(HRM_PERMISSIONS.ATTENDANCE.WAIVE_CORRECTION_FINE);
  const reviewer = useCurrentReviewer();

  // A notification's link lists only its correction.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_ATTENDANCE_CORRECTIONS_ORDER,
    linkedId,
  });
  // The decision picked from a row's menu, waiting for its confirmation.
  const [reviewTarget, setReviewTarget] = useState<{
    correction: AttendanceCorrection;
    action: 'Approved' | 'Rejected';
  } | null>(null);
  const isRejecting = reviewTarget?.action === 'Rejected';
  const [rejectionNote, setRejectionNote] = useState('');
  const { mutateAsync: reviewCorrection } = useReviewAttendanceCorrection();
  const [waiveTarget, setWaiveTarget] = useState<AttendanceCorrection | null>(null);
  const [bulk, setBulk] = useState<{
    itemIds: string[];
    action: 'Approved' | 'Rejected';
    reset?: () => void;
  }>({
    itemIds: [],
    action: 'Approved',
  });
  const { mutateAsync: waiveFine } = useWaiveCorrectionFine();

  // Every filter is a column filter (status, department, dates); this keeps the reviewer's
  // department scope.
  const filters: ManageAttendanceFiltersValue = EMPTY_MANAGE_ATTENDANCE_FILTERS;
  const { departments, getDepartmentIdCondition } = useDepartmentScope();

  // Corrections store no name: the keyword is matched through the employees it finds, and a keyword
  // that finds nobody lists nothing rather than everything.
  const keyword = filters.keyword.trim();
  const keywordSearch = useSearchCorrectionEmployees(keyword, filters.departmentId || undefined);
  const keywordUserIds = keyword
    ? keywordSearch.data?.map((employee) => employee.UserId)
    : undefined;
  const isKeywordEmpty = keywordUserIds?.length === 0;
  const { data, isLoading, error } = useGetAttendanceCorrections(
    {
      pageNo: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filter: mergeWhere(
        buildCorrectionFilter(
          filters,
          getDepartmentIdCondition(filters.departmentId),
          keywordUserIds
        ),
        columnWhere
      ),
      order,
    },
    (!keyword || Boolean(keywordUserIds)) && !isKeywordEmpty
  );
  const result = isKeywordEmpty
    ? undefined
    : (data as { AttendanceCorrections?: AttendanceCorrectionsPagingResult })
        ?.AttendanceCorrections;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  const { byUserId } = useEmployeesByUserIds(
    items.flatMap((correction) => [correction.UserId, correction.ActionTakenBy ?? ''])
  );
  const nameOf = (userId: string) => byUserId.get(userId)?.DisplayName;

  const columns = createCorrectionRequestsTableColumns({
    t,
    canApprove: canReview || canWaive,
    reviewerId: reviewer.itemId,
    selectable: canReview,
    onReview: (correction, action) => {
      setRejectionNote('');
      setReviewTarget({ correction, action });
    },
    onWaiveFine: canWaive ? setWaiveTarget : undefined,
    nameOf,
    emailOf: (userId) => byUserId.get(userId)?.Email,
    departments,
  });

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        getRowId={(row) => row.ItemId}
        canSelectRow={(row) => isReviewable(row, reviewer.itemId)}
        clearSelectionOnPageChange
        columnsToolbar={(table) => {
          if (!canReview) return null;
          const selected = table
            .getSelectedRowModel()
            .rows.map((row) => row.original)
            .filter((correction) => isReviewable(correction, reviewer.itemId));
          if (selected.length === 0) return null;
          const open = (action: 'Approved' | 'Rejected') =>
            setBulk({
              itemIds: selected.map((c) => c.ItemId),
              action,
              reset: () => table.resetRowSelection(),
            });
          return (
            // On a phone the count and the X share the first line, the buttons go under them.
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-primary-50 px-3 py-2">
              <span className="min-w-0 flex-1 text-sm font-medium">
                {t('EMPLOYEES_SELECTED_COUNT', { count: selected.length })}
              </span>
              <div className="order-3 flex basis-full items-center justify-end gap-2 md:order-2 md:basis-auto">
                <Button size="sm" onClick={() => open('Approved')}>
                  {t('APPROVE')}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => open('Rejected')}>
                  {t('REJECT')}
                </Button>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="order-2 size-8 shrink-0 md:order-3"
                aria-label={t('CLEAR_SELECTION')}
                onClick={() => table.resetRowSelection()}
              >
                <X className="size-4" />
              </Button>
            </div>
          );
        }}
        isLoading={isLoading || keywordSearch.isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(reviewTarget)}
        onOpenChange={(open) => !open && setReviewTarget(null)}
        title={t(isRejecting ? 'REJECT_CORRECTION_REQUEST' : 'APPROVE_CORRECTION_REQUEST')}
        description={t(
          isRejecting ? 'REJECT_CORRECTION_CONFIRMATION' : 'APPROVE_CORRECTION_CONFIRMATION'
        )}
        confirmText={isRejecting ? 'REJECT' : 'APPROVE'}
        confirmClassName={isRejecting ? 'destructive' : undefined}
        // The reject workflow requires a reason; it goes to the employee.
        confirmDisabled={isRejecting && !rejectionNote.trim()}
        onConfirm={() =>
          reviewTarget &&
          reviewCorrection({
            itemId: reviewTarget.correction.ItemId,
            status: reviewTarget.action,
            ...(isRejecting ? { note: rejectionNote.trim() } : {}),
          })
        }
      >
        {isRejecting && (
          <div className="space-y-1.5">
            <Label htmlFor="rejection-note">{t('REJECTION_NOTE')}</Label>
            <Textarea
              id="rejection-note"
              rows={3}
              placeholder={t('REJECTION_NOTE')}
              value={rejectionNote}
              onChange={(event) => setRejectionNote(event.target.value)}
            />
          </div>
        )}
      </ConfirmationModal>

      <BulkReviewDialog
        itemIds={bulk.itemIds}
        action={bulk.action}
        onClose={(reviewed) => {
          if (reviewed) bulk.reset?.();
          setBulk((prev) => ({ ...prev, itemIds: [] }));
        }}
      />

      <ConfirmationModal
        open={Boolean(waiveTarget)}
        onOpenChange={(open) => !open && setWaiveTarget(null)}
        title={t('WAIVE_FINE')}
        description={t('WAIVE_FINE_CONFIRMATION', { amount: (waiveTarget?.Fine ?? 0).toFixed(2) })}
        confirmText="WAIVE_FINE"
        onConfirm={() => waiveTarget && waiveFine(waiveTarget.ItemId)}
      />
    </div>
  );
};

export const ManageAttendancePage = () => {
  const { t } = useTranslation();
  // A notification about a correction opens its tab.
  const [tab, setTab] = useLinkedTab('attendance');

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('MANAGE_ATTENDANCE')} />

      <Tabs value={tab} onValueChange={setTab}>
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          <TabsTrigger value="attendance" className="max-sm:flex-1">
            {t('ATTENDANCE')}
          </TabsTrigger>
          <TabsTrigger value="corrections" className="max-sm:flex-1">
            {t('CORRECTION_REQUESTS')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="attendance">
          <AttendanceTab />
        </TabsContent>
        <TabsContent value="corrections">
          <CorrectionRequestsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};
